import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { getAuthConfig } from "@/lib/config";
import { AUTH_CALLBACK_PATH } from "./returnTo";

/**
 * Builds the oidc-client-ts UserManager for admin-v2.
 *
 * Keycloak redirects back to a single dedicated callback route, so the realm can
 * whitelist one exact redirect URI instead of a wildcard. The page the user
 * started on is preserved separately, via the OIDC `state` (see `returnTo`).
 * Tokens are persisted to `sessionStorage` so the full-page round-trip survives.
 *
 * Sign-out sends the user back to the app origin (`post_logout_redirect_uri`),
 * which the realm's webapp client whitelists under `post.logout.redirect.uris`.
 * The trailing slash keeps the URI a strict prefix of the whitelisted `origin/*`
 * pattern. The app has no public landing page - the root route redirects to the
 * dashboard (see `routes/index.tsx`) - so sign-out lands the user straight back
 * on the Keycloak login screen rather than a logged-out state inside the app.
 */
export function createUserManager(): UserManager {
  const { authority, clientId, scope } = getAuthConfig();

  return new UserManager({
    authority,
    client_id: clientId,
    scope,
    redirect_uri: `${window.location.origin}${AUTH_CALLBACK_PATH}`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // automaticSilentRenew inherits the oidc-client-ts default (true), matching v1:
    // renews via the refresh_token grant when available, else a hidden iframe against
    // redirect_uri. A dedicated silent_redirect_uri route is deferred to a later PR.
  });
}

/**
 * Runs after Keycloak redirects back with an authorization code. It strips the
 * `?code=...&state=...` query params from the URL so a refresh does not attempt
 * to replay the (now consumed) code exchange.
 */
export function onSigninCallback(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}
